import { useEffect } from 'react';
import { apiGet } from '../api/client';

interface PendingResponse {
  count: number;
  ids: string[];
}

/**
 * Stage 22: while the Video Gallery is mounted, poll /api/tools/videos/pending
 * every 15 seconds. When the pending-ids set changes (a job finished or a new
 * one started), call the supplied `refresh()` to re-fetch the gallery list so
 * "Generating…" tiles transition to playable thumbnails.
 *
 * 15 s cadence + 60 s cron cadence => worst-case ready-to-visible latency is
 * about 15 seconds, which is acceptable for a multi-minute generation.
 */
export function usePendingVideoPoller(refresh: () => Promise<void>): void {
  useEffect(() => {
    let cancelled = false;
    let lastHash = '';

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      try {
        const res = await apiGet<PendingResponse>('/api/tools/videos/pending');
        const hash = `${res.count}:${res.ids.slice().sort().join(',')}`;
        if (hash !== lastHash) {
          lastHash = hash;
          await refresh();
        }
      } catch {
        // Swallow — next tick tries again.
      }
    };

    void tick();
    const handle = window.setInterval(() => {
      void tick();
    }, 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [refresh]);
}
